// =============================================================================
// FILE: packages/renderer/src/components/stealth/StealthCloak.tsx
// PURPOSE: Full-screen decoy overlay for the Stealth Workspace. Mimics a benign
//   corporate document (Word memo / building-code PDF / estimating spreadsheet)
//   so no proprietary RCD² data is visible. A discreet lock control unlocks via
//   PIN or the operator's login email. A shuffle control swaps the decoy look.
// =============================================================================

import { useState, type JSX } from 'react';
import { createPortal } from 'react-dom';
import { Lock, Shuffle, ShieldCheck } from 'lucide-react';
import { useStealthStore, type DecoyKind } from '../../stores/stealth.store.js';

function WordDecoy(): JSX.Element {
  return (
    <div style={{ background: '#f3f3f3', height: '100%', overflow: 'auto', fontFamily: 'Georgia, serif' }}>
      <div style={{ height: 40, background: '#2b579a', display: 'flex', alignItems: 'center', paddingLeft: 16, color: '#fff', fontFamily: 'Arial', fontSize: 13, gap: 20 }}>
        <span style={{ fontWeight: 700 }}>Document1 — Word</span>
        <span style={{ opacity: 0.85 }}>File</span><span style={{ opacity: 0.85 }}>Home</span><span style={{ opacity: 0.85 }}>Insert</span><span style={{ opacity: 0.85 }}>Layout</span><span style={{ opacity: 0.85 }}>References</span>
      </div>
      <div style={{ maxWidth: 800, margin: '28px auto', background: '#fff', padding: '64px 72px', boxShadow: '0 1px 6px rgba(0,0,0,0.2)', color: '#1a1a1a' }}>
        <h1 style={{ fontSize: 22, textAlign: 'center', marginBottom: 4 }}>Quarterly Operations Memorandum</h1>
        <p style={{ textAlign: 'center', color: '#666', fontSize: 12, marginBottom: 28 }}>Internal Distribution · Confidential</p>
        <p style={{ fontSize: 14, lineHeight: 1.9 }}>To all department leads,</p>
        <p style={{ fontSize: 14, lineHeight: 1.9 }}>Please find below the summary of operational objectives for the upcoming quarter. This memorandum outlines general administrative priorities, scheduling considerations, and standard reporting cadence for the review period.</p>
        <p style={{ fontSize: 14, lineHeight: 1.9 }}>All teams are asked to submit weekly status updates through the shared reporting template. Kindly ensure that timesheets and general correspondence are filed by end of business each Friday.</p>
        <p style={{ fontSize: 14, lineHeight: 1.9 }}>Meeting minutes from the prior session are attached for reference. No action items remain outstanding at this time. A follow-up review will be scheduled at a later date.</p>
        <p style={{ fontSize: 14, lineHeight: 1.9 }}>Thank you for your continued diligence and professionalism.</p>
        <p style={{ fontSize: 14, lineHeight: 1.9, marginTop: 24 }}>Regards,<br />Administration Office</p>
      </div>
    </div>
  );
}

function PdfDecoy(): JSX.Element {
  return (
    <div style={{ background: '#525659', height: '100%', overflow: 'auto' }}>
      <div style={{ height: 40, background: '#323639', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ddd', fontFamily: 'Arial', fontSize: 12 }}>
        Building_Code_Reference.pdf&nbsp;&nbsp;·&nbsp;&nbsp;Page 42 of 318
      </div>
      <div style={{ maxWidth: 760, margin: '26px auto', background: '#fff', padding: '56px 64px', color: '#111', fontFamily: 'Arial', boxShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
        <div style={{ fontSize: 11, color: '#888', letterSpacing: 1 }}>SECTION 3.2 — FIRE SAFETY</div>
        <h2 style={{ fontSize: 17, margin: '6px 0 18px' }}>3.2.4 Egress and Occupant Load Requirements</h2>
        {['3.2.4.1', '3.2.4.2', '3.2.4.3', '3.2.4.4'].map((n, i) => (
          <p key={n} style={{ fontSize: 12.5, lineHeight: 1.8, marginBottom: 12 }}>
            <strong>{n}</strong>&nbsp; Except as permitted by Sentence (2), the occupant load of a floor area or part of a floor area shall be based on the number of persons for which the area is designed, in conformance with the values tabulated in the referenced standard {i === 0 ? 'and shall not exceed the maximum permissible density.' : 'and adjusted for auxiliary spaces as applicable.'}
          </p>
        ))}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5, marginTop: 10 }}>
          <thead><tr style={{ background: '#eee' }}><th style={{ border: '1px solid #ccc', padding: 6, textAlign: 'left' }}>Occupancy</th><th style={{ border: '1px solid #ccc', padding: 6 }}>Area per person (m²)</th><th style={{ border: '1px solid #ccc', padding: 6 }}>Notes</th></tr></thead>
          <tbody>
            {[['Assembly (fixed seats)', '0.75', '(1)'], ['Business & personal services', '9.30', '—'], ['Mercantile', '3.70', '(2)'], ['Industrial', '4.60', '(3)']].map((r) => (
              <tr key={r[0]}><td style={{ border: '1px solid #ccc', padding: 6 }}>{r[0]}</td><td style={{ border: '1px solid #ccc', padding: 6, textAlign: 'center' }}>{r[1]}</td><td style={{ border: '1px solid #ccc', padding: 6, textAlign: 'center' }}>{r[2]}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SheetDecoy(): JSX.Element {
  const cols = ['A', 'B', 'C', 'D', 'E'];
  const rows = Array.from({ length: 22 });
  const data: Record<string, string> = { A1: 'Item', B1: 'Description', C1: 'Qty', D1: 'Unit Cost', E1: 'Total',
    A2: '1001', B2: 'General supplies', C2: '12', D2: '$4.50', E2: '$54.00', A3: '1002', B3: 'Office consumables', C3: '30', D3: '$1.20', E3: '$36.00', A4: '1003', B4: 'Misc hardware', C4: '8', D4: '$12.00', E4: '$96.00' };
  return (
    <div style={{ background: '#fff', height: '100%', overflow: 'auto', fontFamily: 'Calibri, Arial' }}>
      <div style={{ height: 38, background: '#217346', display: 'flex', alignItems: 'center', paddingLeft: 16, color: '#fff', fontSize: 13, gap: 18 }}>
        <span style={{ fontWeight: 700 }}>Budget_Summary.xlsx</span><span style={{ opacity: 0.85 }}>File</span><span style={{ opacity: 0.85 }}>Home</span><span style={{ opacity: 0.85 }}>Insert</span><span style={{ opacity: 0.85 }}>Formulas</span><span style={{ opacity: 0.85 }}>Data</span>
      </div>
      <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
        <thead><tr><th style={{ width: 34, background: '#f3f3f3', border: '1px solid #d4d4d4' }} />{cols.map((c) => <th key={c} style={{ background: '#f3f3f3', border: '1px solid #d4d4d4', padding: '3px 8px', color: '#555', fontWeight: 600, minWidth: 120 }}>{c}</th>)}</tr></thead>
        <tbody>
          {rows.map((_, r) => (
            <tr key={r}>
              <td style={{ background: '#f3f3f3', border: '1px solid #d4d4d4', textAlign: 'center', color: '#555' }}>{r + 1}</td>
              {cols.map((c) => <td key={c} style={{ border: '1px solid #e4e4e4', padding: '3px 8px', color: '#222', fontWeight: r === 0 ? 700 : 400 }}>{data[`${c}${r + 1}`] ?? ''}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const DECOY_MAP: Record<DecoyKind, () => JSX.Element> = { word: WordDecoy, pdf: PdfDecoy, sheet: SheetDecoy };

export function StealthCloak({ userEmail }: { userEmail?: string | null }): JSX.Element | null {
  const { active, decoy, shuffle, tryUnlock } = useStealthStore();
  const [showUnlock, setShowUnlock] = useState(false);
  const [value, setValue] = useState('');
  const [error, setError] = useState(false);

  if (!active) return null;
  const Decoy = DECOY_MAP[decoy] ?? WordDecoy;

  const attempt = (): void => {
    if (tryUnlock(value, userEmail)) { setShowUnlock(false); setValue(''); setError(false); }
    else { setError(true); }
  };

  return createPortal(
    <div data-testid="stealth-cloak" style={{ position: 'fixed', inset: 0, zIndex: 2147483000, background: '#525659' }}>
      <Decoy />

      {/* Discreet controls — blend into a document chrome corner */}
      <div style={{ position: 'fixed', bottom: 14, right: 14, display: 'flex', gap: 8, zIndex: 5 }}>
        <button data-testid="stealth-shuffle" onClick={shuffle} title="Change document"
          style={{ width: 30, height: 30, borderRadius: 6, border: '1px solid rgba(0,0,0,0.15)', background: 'rgba(255,255,255,0.6)', display: 'grid', placeItems: 'center', cursor: 'pointer', color: '#555' }}>
          <Shuffle style={{ width: 14, height: 14 }} />
        </button>
        <button data-testid="stealth-unlock-open" onClick={() => { setShowUnlock(true); setError(false); }} title="Unlock"
          style={{ width: 30, height: 30, borderRadius: 6, border: '1px solid rgba(0,0,0,0.15)', background: 'rgba(255,255,255,0.6)', display: 'grid', placeItems: 'center', cursor: 'pointer', color: '#555' }}>
          <Lock style={{ width: 14, height: 14 }} />
        </button>
      </div>

      {showUnlock && (
        <div onClick={() => setShowUnlock(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 10, background: 'rgba(10,15,25,0.7)', backdropFilter: 'blur(4px)', display: 'grid', placeItems: 'center' }}>
          <div onClick={(e) => e.stopPropagation()} data-testid="stealth-unlock-modal"
            style={{ width: 'min(360px,92%)', borderRadius: 16, background: 'linear-gradient(180deg,#0b1622,#070d16)', border: '1px solid rgba(37,181,168,0.3)', padding: 24, textAlign: 'center' }}>
            <ShieldCheck style={{ width: 34, height: 34, color: '#5eead4', margin: '0 auto 10px' }} />
            <div style={{ color: '#e6fffb', fontSize: 15, fontWeight: 800 }}>Unlock Workspace</div>
            <div style={{ color: 'rgba(148,172,210,0.7)', fontSize: 11.5, margin: '4px 0 16px' }}>Enter your PIN or login email to reveal your work.</div>
            <input data-testid="stealth-unlock-input" autoFocus type="password" value={value}
              onChange={(e) => { setValue(e.target.value); setError(false); }}
              onKeyDown={(e) => { if (e.key === 'Enter') attempt(); }}
              placeholder="PIN or email"
              style={{ width: '100%', padding: '11px 13px', borderRadius: 10, background: 'rgba(8,18,28,0.8)', border: `1px solid ${error ? 'rgba(248,113,113,0.6)' : 'rgba(148,163,184,0.3)'}`, color: '#e2e8f0', fontSize: 14, textAlign: 'center' }} />
            {error && <div data-testid="stealth-unlock-error" style={{ color: '#fca5a5', fontSize: 11.5, marginTop: 8 }}>Incorrect — try your PIN or the email you signed in with.</div>}
            <button data-testid="stealth-unlock-submit" onClick={attempt}
              style={{ width: '100%', marginTop: 16, padding: 11, borderRadius: 10, background: '#2db5a8', border: 'none', color: '#04120f', fontWeight: 800, fontSize: 14, cursor: 'pointer' }}>Reveal my workspace</button>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}
